import type { FC } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import qs from 'qs'
import { FinancingProductsOther } from 'src/pages/Financing/components/FinancingProductsOther'
import type { ViewByOptionsQueryParams } from '@/api/kubik/credit'
import { useViewByOptions } from '@/api/kubik/credit'
import { history } from '@/browserHistory'
import { FinancingConsulting } from '@/components/composed/financing'
import { LandingAgentBanner } from '@/components/composed/landingBlocks/LandingAgentBanner'
import { getLocation } from '@/logic/app/reducer'
import { useGtm } from '@/logic/metrika/financing/gtmHelpers'
import { EGtmLabel, EGtmProductName } from '@/logic/metrika/financing/types'
import { FinancingConsultationPrompt } from '@/pages/Financing/components/FinancingConsultationPrompt/FinancingConsultationPrompt'
import { FinancingMobileBanner } from '@/pages/Financing/components/FinancingMobileBanner'
import { FinancingProductPopups } from '@/pages/Financing/components/FinancingProductPopups'
import type { IFinancingProps, IProductsListContext } from '@/pages/Financing/types'
import type { RootState } from '@/redux/interfaces'
import { scrollTo } from '@/utils/helpers'
import { useBlockIntersection, useMount } from '@/utils/hooks'
import { FinancingAccessControlProvider } from './components/FinancingAccessControlProvider'
import { DEFAULT_FORM_VALUES } from './components/FinancingProducts/components/LoanProducts/components/LoanProductsFilter/constants'
import type { ILoanProductsFilterForm } from './components/FinancingProducts/components/LoanProducts/components/LoanProductsFilter/types'
import {
  convertFormValuesToQueryString,
  convertFormValuesToReactQuery,
  convertQueryParamsToFormValues,
} from './components/FinancingProducts/components/LoanProducts/components/LoanProductsFilter/utils'
import { FinancingBreadcrumbs, FinancingFaq, FinancingProducts, FinancingResponses } from './components'
import { defaultContextValue, EFinancingCategory, ProductsListContext } from './constants'
import './styles.scss'

const mapState = (state: RootState) => ({
  location: getLocation()(state),
})

const Financing: FC<IFinancingProps> = ({ isLoanCalculatorShown }) => {
  const { location } = useSelector(mapState)
  const { pushGtmPageView, pushGtmFormView } = useGtm()
  const [isConsultationVisible, setIsConsultationVisible] = useState(false)
  const { search: searchQuery } = useLocation()
  const queryParams = qs.parse(searchQuery.substring(1))

  const productsListIntersection = useBlockIntersection({ shouldKeepIntersection: true, threshold: 0.2 })
  const otherProductsListIntersection = useBlockIntersection({ shouldKeepIntersection: true, threshold: 0.2 })
  const productTabsIntersection = useBlockIntersection({ shouldKeepIntersection: true, threshold: 1 })

  const activeTab = queryParams.tab ?? EFinancingCategory.LOANS

  const initialValues = useMemo<ILoanProductsFilterForm>(
    () => {
      const convertedQueryParams = convertQueryParamsToFormValues(queryParams)

      return {
        ...DEFAULT_FORM_VALUES,
        ...convertedQueryParams,
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const form = useForm<ILoanProductsFilterForm>({
    defaultValues: initialValues,
  })

  const [queryValues, setQueryValues] = useState<ViewByOptionsQueryParams>(() => convertFormValuesToReactQuery(form.getValues()))

  const formValues = form.watch()

  const isFormChanged = useMemo(
    () => !isEqual(formValues, { ...DEFAULT_FORM_VALUES, regionCode: '' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formValues.requiredAmount,
      formValues.loanPeriod,
      formValues.regionCode,
      formValues.options,
      formValues.processingSpeed,
      formValues.fundingPurpose,
    ],
  )

  const { data, isLoading } = useViewByOptions(queryValues)

  const productsListContextValue: IProductsListContext = useMemo(
    () => ({
      productsList: productsListIntersection,
      otherProductsList: otherProductsListIntersection,
      productTabs: productTabsIntersection,
      products: data ?? defaultContextValue.products,
      isLoading,
      isFilterChanged: isFormChanged,
    }),
    [productTabsIntersection, productsListIntersection, otherProductsListIntersection, data, isLoading, isFormChanged],
  )

  const onFormChange = useMemo(
    () =>
      debounce((values: ILoanProductsFilterForm) => {
        const search = convertFormValuesToQueryString(values, DEFAULT_FORM_VALUES)
        const reactQueryValues = convertFormValuesToReactQuery(values)

        history.replace({ search })
        setQueryValues(reactQueryValues)
      }, 250),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(
    () => {
      onFormChange(formValues)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formValues.requiredAmount,
      formValues.loanPeriod,
      formValues.regionCode,
      formValues.options,
      formValues.processingSpeed,
      formValues.fundingPurpose,
    ],
  )

  const scrollHandler = () => {
    const isVisible = window.scrollY === 0

    if (isConsultationVisible !== isVisible) {
      if (isVisible) pushGtmFormView(EGtmProductName.MARKETPLACE)
      setIsConsultationVisible(isVisible)
    }
  }

  useMount(
    () => {
      if (location.hash) scrollTo(location.hash)
      if (history.action === 'PUSH') pushGtmPageView(EGtmLabel.EXPERIMENTAL_GROUP)
      scrollHandler()
      window.addEventListener('scroll', scrollHandler)
    },
    () => window.removeEventListener('scroll', scrollHandler),
  )

  return (
    <FinancingAccessControlProvider>
      <FinancingConsultationPrompt />
      <FormProvider {...form}>
        <ProductsListContext.Provider value={productsListContextValue}>
          <FinancingMobileBanner />
          <div className="page-wrapper">
            <FinancingBreadcrumbs />
            <FinancingConsulting />
            <FinancingProducts isLoanCalculatorShown={isLoanCalculatorShown} />
            <FinancingProductsOther isVisible={activeTab === EFinancingCategory.LOANS} />
            <FinancingFaq />
            <FinancingResponses />
            <FinancingConsulting />
          </div>
        </ProductsListContext.Provider>

        <LandingAgentBanner className="financingPage__landingAgentBanner" />
        <FinancingProductPopups />
      </FormProvider>
    </FinancingAccessControlProvider>
  )
}

export default Financing
